import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { ShieldAlert, RefreshCw, Loader2, Settings, ChevronDown, Crown } from 'lucide-react';
import { formatDate } from '../utils/dateFormatter';
import { StatusBadge, FrameworkLine, NextStepsBox } from './directoryWidgetCommon';
import FindingCrossLinks from './FindingCrossLinks';

const PrivilegedAccountsWidget = ({ workspaceId, gwConnected }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openSection, setOpenSection] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [capInput, setCapInput] = useState('4');
  const [savingSettings, setSavingSettings] = useState(false);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    try {
      setLoading(true);
      const resp = await axios.get('/api/employees/privileged-accounts', {
        params: { workspace_id: workspaceId },
      });
      setData(resp.data);
      setCapInput(String(resp.data.super_admin_cap || 4));
    } catch (err) {
      console.error('privileged-accounts load failed', err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const handleSaveCap = async () => {
    try {
      setSavingSettings(true);
      const v = parseInt(capInput, 10);
      if (!Number.isFinite(v)) return;
      await axios.put('/api/workspace-settings/inactive-accounts',
        { super_admin_cap: v },
        { params: { workspace_id: workspaceId } });
      toast.success('Super-admin cap updated');
      setSettingsOpen(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update cap');
    } finally {
      setSavingSettings(false);
    }
  };

  if (!gwConnected) return null;

  const rules = data?.rules || {};
  const overall = (() => {
    const statuses = Object.values(rules).map(r => r.status);
    if (statuses.includes('failed')) return 'failed';
    if (statuses.includes('insufficient_evidence')) return 'insufficient_evidence';
    if (statuses.length && statuses.every(s => s === 'passed')) return 'passed';
    return 'not_applicable';
  })();
  const t = data?.totals || {};

  return (
    <Card className="border-slate-200 mb-6" data-testid="privileged-accounts-widget">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Crown className="w-5 h-5 text-slate-500" strokeWidth={1.7} />
              Privileged Account Governance
              {data && <StatusBadge status={overall} />}
            </CardTitle>
            <FrameworkLine>
              SOC 2 CC6.3 · ISO 27001 A.5.15/A.5.18
            </FrameworkLine>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={load} variant="ghost" size="sm" disabled={loading}
                    data-testid="priv-refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button onClick={() => setSettingsOpen(true)} variant="outline" size="sm"
                    data-testid="priv-settings-btn">
              <Settings className="w-4 h-4 mr-1" />
              cap {data?.super_admin_cap || 4}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {loading && !data && (
          <div className="py-6 text-center text-sm text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
            Evaluating directory…
          </div>
        )}

        {data && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4">
              {[
                { key: 'super_admin_count_cap', label: 'Super-admin cap', value: `${t.super_admins}/${t.active_users}` },
                { key: 'admin_2sv_required',    label: 'Admins on 2SV',    value: `${(t.admins_total || 0) - (t.admins_without_2sv || 0)}/${t.admins_total || 0}` },
                { key: 'role_assignment_inventory', label: 'Role assignments', value: `${t.role_assignments_total || 0} · ${t.distinct_roles || 0} role(s)` },
              ].map(({ key, label, value }) => {
                const rule = rules[key] || {};
                const tone = rule.status === 'failed'
                  ? 'bg-red-50 border-red-200 text-red-900'
                  : rule.status === 'insufficient_evidence'
                  ? 'bg-amber-50 border-amber-200 text-amber-900'
                  : 'bg-emerald-50 border-emerald-200 text-emerald-900';
                const isOpen = openSection === key;
                return (
                  <button
                    type="button"
                    key={key}
                    onClick={() => setOpenSection(isOpen ? null : key)}
                    className={`border rounded-lg p-3 text-left transition-all hover:shadow-sm ${tone} ${isOpen ? 'ring-2 ring-offset-1 ring-slate-300' : ''}`}
                    data-testid={`priv-tile-${key}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <ShieldAlert className="w-4 h-4" strokeWidth={1.7} />
                      <span className="text-base font-semibold leading-none">{value}</span>
                    </div>
                    <div className="text-xs font-medium">{label}</div>
                    <div className="mt-1 text-[11px] flex items-center gap-1">
                      <StatusBadge status={rule.status} />
                      <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </div>
                  </button>
                );
              })}
            </div>

            {openSection && rules[openSection] && (
              <div className="border rounded-lg border-slate-200 bg-white p-3 mb-3 text-sm" data-testid="priv-drilldown">
                <div className="font-medium text-slate-800 mb-1">
                  {{
                    super_admin_count_cap: 'Super-admin count cap',
                    admin_2sv_required: 'Admins / privileged users on 2SV',
                    role_assignment_inventory: 'Admin role assignment inventory',
                  }[openSection]}
                </div>
                <p className="text-slate-700">{rules[openSection].proof}</p>

                {openSection === 'super_admin_count_cap' && (data.super_admin_users || []).length > 0 && (
                  <div className="mt-2 divide-y divide-slate-100 text-xs">
                    {data.super_admin_users.map((u) => (
                      <div key={u.primary_email} className="py-1 flex items-center justify-between">
                        <span className="text-slate-800">{u.full_name || u.primary_email}</span>
                        <span className="text-slate-500">{u.primary_email}</span>
                        {!u.is_enrolled_in_2sv && (
                          <Badge className="bg-red-100 text-red-800 border-red-200 text-[10px]">No 2SV</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {openSection === 'admin_2sv_required' && (data.admins_without_2sv || []).length > 0 && (
                  <div className="mt-2 divide-y divide-slate-100 text-xs">
                    {data.admins_without_2sv.map((u) => (
                      <div key={u.source_user_id} className="py-1">
                        <div className="font-medium text-slate-900">{u.full_name || u.primary_email}</div>
                        <div className="text-slate-500">{u.primary_email}</div>
                      </div>
                    ))}
                  </div>
                )}

                {openSection === 'role_assignment_inventory' && (data.role_summary || []).length > 0 && (
                  <div className="mt-2 divide-y divide-slate-100 text-xs">
                    {data.role_summary.map((r) => (
                      <div key={r.role_name} className="py-1 flex items-center justify-between">
                        <span className="text-slate-800">
                          {r.role_name}
                          {r.is_super_admin_role && (
                            <Badge className="ml-2 bg-red-100 text-red-800 border-red-200 text-[10px]">Super admin</Badge>
                          )}
                        </span>
                        <span className="text-slate-500">{r.assignees.length} assignee(s)</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <NextStepsBox text={
              Object.values(rules).map(r => r.what_would_fix).filter(Boolean).join(' ')
            } />

            <FindingCrossLinks
              ruleKey="directory.access.admin_2sv_required"
              matches={(data.admins_without_2sv || []).map(u => ({ email: u.primary_email }))}
              workspaceId={workspaceId}
            />

            <div className="mt-3 text-[11px] text-slate-400">
              Evaluated {formatDate(data.evaluated_at)} · {t.active_users} active users
            </div>
          </>
        )}
      </CardContent>

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Super-Admin Cap</DialogTitle>
            <DialogDescription>
              Maximum number of Google Workspace super-admins permitted before the
              "Super-admin count cap" rule flags as a finding. ≤4 super-admins
              is a recommended starting point.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label htmlFor="cap-input" className="text-xs">Super-admin cap</Label>
            <Input id="cap-input" type="number" min="1" max="25"
                   value={capInput}
                   onChange={(e) => setCapInput(e.target.value)}
                   className="mt-1" data-testid="cap-input" />
            <p className="text-[11px] text-slate-500 mt-1">1 – 25.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveCap} disabled={savingSettings}
                    className="bg-navy-600 hover:bg-navy-700"
                    data-testid="cap-save-btn">
              {savingSettings ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default PrivilegedAccountsWidget;
